import { useCallback, useEffect, useRef, useState } from "react";
import { Trans } from "@lingui/react/macro";
import { useLingui } from "@lingui/react";
import { FileText, Upload, Loader2, X, ExternalLink, XCircle, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { FormLabel } from "@/components/ui/form";
import { Progress } from "@/components/ui/progress";
import { useImageUpload } from "@/hooks/useImageUpload";
import { images_GenerateUploadURLRequest } from "@/api/models/images_GenerateUploadURLRequest";
import { formatFileSize } from "@/config/storage";
import { cn } from "@/lib/utils";
import { msg } from "@lingui/macro";

interface UploadedFile {
  fileId: string;
  fileName: string;
  /** Blob URL for local preview of newly uploaded files */
  previewUrl?: string;
}

interface ExistingFile {
  fileName: string;
  version: number;
  downloadUrl?: string;
}

interface DocumentFileSectionProps {
  /** Currently uploaded (unsaved) file */
  uploadedFile: UploadedFile | null;
  /** Existing file already attached to the document */
  existingFile?: ExistingFile | null;
  /** Called when a file finishes uploading */
  onFileUploaded: (file: UploadedFile) => void;
  /** Called when the uploaded file is removed */
  onFileRemoved: () => void;
}

const ACCEPT = ".pdf,.doc,.docx,.xls,.xlsx,.txt,.csv";

export function DocumentFileSection({
  uploadedFile,
  existingFile,
  onFileUploaded,
  onFileRemoved,
}: DocumentFileSectionProps) {
  const { i18n } = useLingui();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [clearedExisting, setClearedExisting] = useState(false);

  // Reset cleared state when existing file changes (e.g., different document opened)
  useEffect(() => {
    setClearedExisting(false);
  }, [existingFile?.fileName]);

  const { uploadFile, uploadProgress, isUploading, reset, cancelUpload } = useImageUpload({
    purpose: images_GenerateUploadURLRequest.purpose.DOCUMENT,
    onSuccess: (imageId) => {
      // We stored the pending file name before starting, now complete it
      if (pendingFileRef.current) {
        setClearedExisting(false);
        onFileUploaded({
          fileId: imageId,
          fileName: pendingFileRef.current.name,
          previewUrl: pendingFileRef.current.previewUrl,
        });
      }
    },
    onError: () => {
      // Clean up preview URL on error
      if (pendingFileRef.current?.previewUrl) {
        URL.revokeObjectURL(pendingFileRef.current.previewUrl);
      }
      pendingFileRef.current = null;
      reset();
    },
  });

  // Track the file being uploaded so we can get its name on success
  const pendingFileRef = useRef<{ name: string; previewUrl?: string } | null>(null);

  const handleFileSelect = useCallback(async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const file = files[0];

    // Create blob URL for preview
    const previewUrl = URL.createObjectURL(file);
    pendingFileRef.current = { name: file.name, previewUrl };

    await uploadFile(file);
  }, [uploadFile]);

  const handleRemove = useCallback(() => {
    if (uploadedFile) {
      // Revoke blob URL if present
      if (uploadedFile.previewUrl) {
        URL.revokeObjectURL(uploadedFile.previewUrl);
      }
      pendingFileRef.current = null;
      reset();
      onFileRemoved();
    } else {
      // Clearing existing file — show upload zone for replacement
      setClearedExisting(true);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, [uploadedFile, reset, onFileRemoved]);

  const handleCancel = useCallback(() => {
    cancelUpload();
    if (pendingFileRef.current?.previewUrl) {
      URL.revokeObjectURL(pendingFileRef.current.previewUrl);
    }
    pendingFileRef.current = null;
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, [cancelUpload]);

  const handlePreview = useCallback((url: string) => {
    window.open(url, "_blank", "noopener,noreferrer");
  }, []);

  // Determine what file to show: newly uploaded takes precedence, cleared existing hides it
  const displayFile = uploadedFile
    ? { name: uploadedFile.fileName, previewUrl: uploadedFile.previewUrl, isNew: true }
    : existingFile?.fileName && !clearedExisting
      ? { name: existingFile.fileName, version: existingFile.version, downloadUrl: existingFile.downloadUrl, isNew: false }
      : null;

  return (
    <div>
      <FormLabel>
        <Trans id="documents.fields.file">File</Trans>
      </FormLabel>

      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPT}
        onChange={(e) => handleFileSelect(e.target.files)}
        disabled={isUploading}
        className="sr-only"
      />

      {/* File info card */}
      {displayFile && !isUploading && (
        <div className="flex items-center gap-2 mt-1 rounded-md border p-2 text-sm">
          <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
          <span className="truncate flex-1">{displayFile.name}</span>
          {!displayFile.isNew && displayFile.version && (
            <span className="text-xs text-muted-foreground whitespace-nowrap">
              (v{displayFile.version})
            </span>
          )}
          {displayFile.isNew && (
            <span className="text-xs text-success-on-surface whitespace-nowrap">
              <Trans id="documents.file.new">New</Trans>
            </span>
          )}
          {/* Preview button */}
          {(displayFile.isNew ? displayFile.previewUrl : displayFile.downloadUrl) && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-7 w-7 shrink-0"
              onClick={() => {
                const url = displayFile.isNew ? displayFile.previewUrl : displayFile.downloadUrl;
                if (url) handlePreview(url);
              }}
              aria-label={i18n._({
                id: "documents.file.preview",
                message: "Preview file",
              })}
            >
              <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
            </Button>
          )}
          {/* Remove button */}
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-7 w-7 shrink-0 text-destructive hover:text-destructive"
            onClick={handleRemove}
            aria-label={i18n._({
              id: "documents.file.remove",
              message: "Remove file",
            })}
          >
            <X className="h-3.5 w-3.5" aria-hidden="true" />
          </Button>
        </div>
      )}

      {/* Upload New Version button — shown when an existing file is attached */}
      {displayFile && !displayFile.isNew && !isUploading && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="mt-2 w-full"
          onClick={() => fileInputRef.current?.click()}
        >
          <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
          <Trans id="documents.file.uploadNewVersion">Upload New Version</Trans>
        </Button>
      )}

      {/* Upload progress */}
      {isUploading && (
        <div className="mt-1 rounded-md border p-3 space-y-2">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            <span>{getUploadStatusText(uploadProgress.status, i18n)}</span>
          </div>
          <Progress value={uploadProgress.progress} />
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleCancel}
          >
            <XCircle className="h-4 w-4 mr-1" />
            <Trans id="common.cancel">Cancel</Trans>
          </Button>
        </div>
      )}

      {/* Upload error */}
      {uploadProgress.error && !isUploading && (
        <p className="text-sm text-destructive mt-1">{uploadProgress.error}</p>
      )}

      {/* Drop zone — show when no file displayed and not uploading */}
      {!displayFile && !isUploading && (
        <div
          className={cn(
            "mt-1 flex flex-col items-center justify-center rounded-lg border-2 border-dashed p-6 cursor-pointer",
            "border-muted-foreground/25 hover:border-muted-foreground/50 transition-colors",
          )}
          role="button"
          tabIndex={0}
          aria-label={i18n._(msg`documents.file.upload`)}
          onClick={() => fileInputRef.current?.click()}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              fileInputRef.current?.click();
            }
          }}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault();
            handleFileSelect(e.dataTransfer.files);
          }}
        >
          <Upload className="h-8 w-8 text-muted-foreground mb-2" />
          <p className="text-sm font-medium">
            <Trans id="imageUpload.clickToUpload">Click to upload or drag and drop</Trans>
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            {i18n._(
              msg({
                id: 'imageUpload.maxFileSize',
                message: 'Maximum file size: {maxSize}',
              }),
              { maxSize: formatFileSize(50 * 1024 * 1024) }
            )}
          </p>
        </div>
      )}
    </div>
  );
}

function getUploadStatusText(
  status: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  i18n: { _: (message: any, values?: any) => string },
): string {
  switch (status) {
    case "generating":
      return i18n._(msg`imageUpload.status.preparing`);
    case "uploading":
      return i18n._(msg`imageUpload.status.uploading`);
    case "confirming":
      return i18n._(msg`imageUpload.status.confirming`);
    default:
      return "";
  }
}
